import React, { useState } from 'react';
import { X, Search, Loader2, Mail, UserPlus, CheckCircle2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService, { getActiveSponsorId } from '../../services/caregiverService';
import RelationshipTypeSelect from './RelationshipTypeSelect';
import CaregiverStatusBadge from './statusBadge';
import CaregiverSetupModal from './CaregiverSetupModal';

/**
 * Add Caregiver modal — drives both the search-first flow (FECOA-1369) and the
 * create-new flow (FECOA-1362). The modal walks through these states:
 *
 *   search   →  results / no-match
 *      └─ link_form (existing caregiver)  → success
 *      └─ create_form (new caregiver)     → success
 */
const STEP_SEARCH = 'search';
const STEP_LINK_FORM = 'link_form';
const STEP_CREATE_FORM = 'create_form';
const STEP_SUCCESS = 'success';

const initialCreate = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  relationship_type: '',
  invitation_method: 'email',
  temp_pin: '',
  informed_consent_verified: false,
};

// Strip anything that isn't a phone-friendly character. Accepts digits, +, -,
// (, ), space, and dot — every common North American / international format.
const sanitizePhoneInput = (raw) => (raw || '').replace(/[^\d+\-() .]/g, '');

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
// Accept anything with at least 7 digits so we don't reject international or
// extension-bearing numbers, but block obvious junk (e.g. all letters).
const isValidPhone = (value) => (value.match(/\d/g) || []).length >= 7;

// Shared field classes (src/styles/tailwind.css) so the form matches the rest
// of the site in both color modes.
const inputCls = (hasError) => `input ${hasError ? 'input-error' : ''}`;
// Small uppercase label above a read-only value on the success summary.
const VALUE_LABEL = 'text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400';
// Bordered option box around a checkbox and its description.
const OPTION_BOX = 'flex items-start gap-2.5 rounded-lg border bg-gray-50 p-3 cursor-pointer dark:bg-gray-900/40';
// Invitation method choice cards.
const METHOD_CARD = 'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60';
const METHOD_ON = 'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-500/10';
const METHOD_OFF = 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-white/[0.03]';

/**
 * Props:
 *   - participantId / siteId  — required when persisting (default mode)
 *   - studyCode               - the participant's Study Code, shown on the
 *                               in-clinic setup card for the caregiver to sign
 *                               in with (FECOA-2156)
 *   - onClose                 — fired on cancel / X
 *   - onCreated               — fired after successful link/create (default mode)
 *   - deferred (bool)         — if true, the modal does NOT call the API. It
 *                               just returns the assembled payload via onQueue
 *                               and closes. Used by Add Participant to queue
 *                               caregivers before the participant exists.
 *   - onQueue(item)           — callback for deferred mode. `item` shape:
 *                                 { kind: 'link', caregiver, relationship_type }
 *                                 { kind: 'create', payload, summary }
 */
const AddCaregiverModal = ({ participantId, participantLabel, siteId, studyCode, onClose, onCreated, deferred = false, onQueue }) => {
  const [step, setStep] = useState(STEP_SEARCH);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null); // null = not searched yet
  const [matchedCaregiver, setMatchedCaregiver] = useState(null);
  const [linkForm, setLinkForm] = useState({
    relationship_type: '',
    informed_consent_verified: false,
  });
  const [createForm, setCreateForm] = useState(initialCreate);
  const [createErrors, setCreateErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successPayload, setSuccessPayload] = useState(null);

  const close = () => {
    if (!submitting) onClose && onClose();
  };

  // -- Step 1: Search --

  const runSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const data = await caregiverService.searchExisting(searchQuery.trim());
      setMatches(data.matches || []);
    } catch (err) {
      console.error('search failed', err);
      toast.error('Search failed.');
      setMatches([]);
    } finally {
      setSearching(false);
    }
  };

  const pickMatch = (cg) => {
    setMatchedCaregiver(cg);
    setStep(STEP_LINK_FORM);
  };

  const switchToCreate = () => {
    setCreateForm({
      ...initialCreate,
      // Pre-fill if the search query looked like an email or ID
      email: searchQuery.includes('@') ? searchQuery : '',
    });
    setStep(STEP_CREATE_FORM);
  };

  // -- Step 2a: Link existing --

  const submitLink = async () => {
    if (!linkForm.relationship_type || !linkForm.informed_consent_verified) {
      toast.error('Relationship and ICF confirmation are required.');
      return;
    }
    // Deferred mode: hand the queue item off and close. The caller will
    // call linkToParticipant once the participant actually exists.
    if (deferred) {
      onQueue && onQueue({
        kind: 'link',
        caregiver: matchedCaregiver,
        relationship_type: linkForm.relationship_type,
      });
      onClose && onClose();
      return;
    }
    setSubmitting(true);
    try {
      await caregiverService.linkToParticipant(matchedCaregiver.id, {
        participant_id: participantId,
        site_id: siteId,
        relationship_type: linkForm.relationship_type,
        informed_consent_verified: true,
      });
      setSuccessPayload({
        kind: 'linked',
        caregiver: matchedCaregiver,
        relationship_type: linkForm.relationship_type,
      });
      setStep(STEP_SUCCESS);
    } catch (err) {
      console.error('link failed', err);
      // 409 means the caregiver is already linked to this participant in some
      // active state (Active / Pending First Login / Locked).
      if (err?.response?.status === 409) {
        toast.error(
          err.response.data?.detail || 'This caregiver is already linked to this participant.',
          { duration: 6000 }
        );
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to link caregiver.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -- Step 2b: Create new --

  const submitCreate = async () => {
    const f = createForm;
    const errors = {};
    if (!f.first_name.trim()) errors.first_name = 'First name is required.';
    if (!f.last_name.trim()) errors.last_name = 'Last name is required.';
    if (!f.relationship_type) errors.relationship_type = 'Relationship is required.';

    const emailRequired = f.invitation_method === 'email';
    if (emailRequired && !f.email.trim()) {
      errors.email = 'Email is required when sending an Email Invitation.';
    } else if (f.email.trim() && !isValidEmail(f.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }

    if (f.phone.trim() && !isValidPhone(f.phone)) {
      errors.phone = 'Enter a valid phone number (at least 7 digits).';
    }

    if (!f.informed_consent_verified) {
      errors.informed_consent_verified = 'Informed consent must be confirmed.';
    }

    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    const payload = {
      first_name: f.first_name,
      last_name: f.last_name,
      email: f.email || null,
      phone: f.phone || null,
      participant_id: participantId,
      site_id: siteId,
      relationship_type: f.relationship_type,
      invitation_method: f.invitation_method,
      informed_consent_verified: true,
    };

    // Deferred mode: queue the create payload for the parent to execute later.
    // Note: participant_id / site_id will be filled in by the caller at
    // dispatch time (they're not known yet — the participant doesn't exist).
    if (deferred) {
      onQueue && onQueue({
        kind: 'create',
        payload,
        summary: {
          full_name: `${f.first_name} ${f.last_name}`.trim(),
          email: f.email || null,
          relationship_type: f.relationship_type,
          invitation_method: f.invitation_method,
          // The in-clinic PIN is generated server-side at dispatch time, so it
          // is not known while the create is queued (FECOA-1429).
          temp_pin: null,
        },
      });
      onClose && onClose();
      return;
    }

    setSubmitting(true);
    try {
      const result = await caregiverService.create(payload);
      setSuccessPayload({
        kind: 'created',
        caregiver: result,
        invitation_method: f.invitation_method,
        relationship_type: f.relationship_type,
        // Held only for the in-clinic flow so the success screen can show the
        // setup QR/PIN (FECOA-1429). Generated server-side and returned once in
        // the create response; never re-fetchable after this screen closes.
        temp_pin: result?.temporary_pin || null,
      });
      setStep(STEP_SUCCESS);
    } catch (err) {
      console.error('create failed', err);
      if (err?.response?.status === 409) {
        toast.error(
          err.response.data?.detail || 'A caregiver is already linked to this participant.',
          { duration: 6000 }
        );
      } else {
        const detail =
          err?.response?.data?.detail ||
          Object.values(err?.response?.data || {})[0] ||
          'Failed to create caregiver.';
        toast.error(typeof detail === 'string' ? detail : 'Failed to create caregiver.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -- Render helpers --

  const Header = ({ title, subtitle }) => (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      <button
        onClick={close}
        aria-label="Close"
        className="btn btn-ghost btn-sm btn-icon -mr-2 -mt-1"
      >
        <X className="h-[18px] w-[18px]" />
      </button>
    </div>
  );

  const renderSearch = () => (
    <>
      <Header
        title="Add Caregiver"
        subtitle="Search the global database first. If the caregiver doesn't exist, you'll be able to create a new profile."
      />
      <label className="label">
        Search Global Caregiver Database
      </label>
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (searchQuery.trim() && !searching) runSearch();
              }
            }}
            placeholder="Search by the exact email or caregiver ID..."
            className={`pl-10 pr-3 ${inputCls(false)}`}
          />
        </div>
        <button
          type="button"
          onClick={runSearch}
          disabled={!searchQuery.trim() || searching}
          className="btn btn-primary h-10 min-w-[5.5rem]"
        >
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </button>
      </div>
      {matches === null && (
        <p className="py-6 text-center text-xs text-gray-500 dark:text-gray-400">
          Enter the exact email or caregiver ID to find an existing profile.
        </p>
      )}
      {matches !== null && matches.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-900/40">
          <p className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">No existing caregiver found</p>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            No match for &quot;{searchQuery}&quot; in the global database.
          </p>
          <button
            type="button"
            onClick={switchToCreate}
            className="btn btn-primary"
          >
            <UserPlus className="h-4 w-4" />
            Create New Caregiver Profile
          </button>
        </div>
      )}
      {matches && matches.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-primary-700 dark:text-primary-400">
            {matches.length} match{matches.length === 1 ? '' : 'es'} found in global database
          </p>
          {matches.map((cg) => (
            <div
              key={cg.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {cg.full_name || `${cg.first_name || ''} ${cg.last_name || ''}`.trim() || cg.caregiver_id}
                </p>
                {cg.email && <p className="text-xs text-gray-500 dark:text-gray-400">{cg.email}</p>}
                <p className="font-mono text-xs text-primary-700 dark:text-primary-400">{cg.caregiver_id}</p>
              </div>
              <button
                type="button"
                onClick={() => pickMatch(cg)}
                className="btn btn-primary btn-sm flex-shrink-0"
              >
                Link to Participant
              </button>
            </div>
          ))}
          <p className="pt-2 text-center text-xs text-gray-500 dark:text-gray-400">
            Not the right person?{' '}
            <button
              type="button"
              onClick={switchToCreate}
              className="link"
            >
              Create a new caregiver profile instead
            </button>
          </p>
        </div>
      )}
    </>
  );

  const renderLinkForm = () => (
    <>
      <Header title="Add Caregiver" subtitle={`Link existing caregiver to this participant`} />
      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {matchedCaregiver?.full_name ||
            `${matchedCaregiver?.first_name || ''} ${matchedCaregiver?.last_name || ''}`.trim()}
        </p>
        {matchedCaregiver?.email && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{matchedCaregiver.email}</p>
        )}
        <p className="font-mono text-xs text-primary-700 dark:text-primary-400">
          {matchedCaregiver?.caregiver_id}
        </p>
      </div>

      <label className="label">
        Relationship to Participant *
      </label>
      <RelationshipTypeSelect
        value={linkForm.relationship_type}
        onChange={(v) => setLinkForm({ ...linkForm, relationship_type: v })}
      />
      <p className="hint mb-4">
        This relationship applies only to this study. The caregiver&rsquo;s global profile is unchanged.
      </p>

      <label className={`${OPTION_BOX} mb-5 border-gray-200 dark:border-gray-700`}>
        <input
          type="checkbox"
          checked={linkForm.informed_consent_verified}
          onChange={(e) =>
            setLinkForm({ ...linkForm, informed_consent_verified: e.target.checked })
          }
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded"
        />
        <span className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-gray-100">Informed Consent Verified *</strong>
          <br />
          I confirm that informed consent has been obtained and properly recorded for this caregiver to participate in this study.
        </span>
      </label>

      <div className="flex justify-between gap-2">
        <button
          type="button"
          onClick={() => setStep(STEP_SEARCH)}
          className="btn btn-secondary"
        >
          Back to Results
        </button>
        <button
          type="button"
          onClick={submitLink}
          disabled={submitting || !linkForm.informed_consent_verified || !linkForm.relationship_type}
          className="btn btn-primary"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm & Link Caregiver'}
        </button>
      </div>
    </>
  );

  const renderCreateForm = () => (
    <>
      <Header
        title="Add Caregiver"
        subtitle="Create new caregiver profile for this participant"
      />
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label">First Name *</label>
          <input
            type="text"
            name="first_name"
            value={createForm.first_name}
            onChange={(e) => {
              setCreateForm({ ...createForm, first_name: e.target.value });
              if (createErrors.first_name) setCreateErrors({ ...createErrors, first_name: undefined });
            }}
            className={inputCls(!!createErrors.first_name)}
          />
          {createErrors.first_name && <p className="field-error">{createErrors.first_name}</p>}
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input
            type="text"
            name="last_name"
            value={createForm.last_name}
            onChange={(e) => {
              setCreateForm({ ...createForm, last_name: e.target.value });
              if (createErrors.last_name) setCreateErrors({ ...createErrors, last_name: undefined });
            }}
            className={inputCls(!!createErrors.last_name)}
          />
          {createErrors.last_name && <p className="field-error">{createErrors.last_name}</p>}
        </div>
      </div>

      <label className="label">
        Email{createForm.invitation_method === 'email' ? ' *' : ''}
      </label>
      <input
        type="email"
        value={createForm.email}
        onChange={(e) => {
          setCreateForm({ ...createForm, email: e.target.value });
          if (createErrors.email) setCreateErrors({ ...createErrors, email: undefined });
        }}
        className={inputCls(!!createErrors.email)}
      />
      {createErrors.email ? (
        <p className="field-error mb-3">{createErrors.email}</p>
      ) : (
        <p className="hint mb-3">
          {createForm.invitation_method === 'email'
            ? 'Required for the Email Invitation flow.'
            : 'Optional. In-clinic caregivers sign in with the Study Code, their Caregiver ID and PIN.'}
        </p>
      )}

      <label className="label">Phone</label>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={createForm.phone}
        onChange={(e) => {
          setCreateForm({ ...createForm, phone: sanitizePhoneInput(e.target.value) });
          if (createErrors.phone) setCreateErrors({ ...createErrors, phone: undefined });
        }}
        placeholder="(555) 555-5555"
        className={`${inputCls(!!createErrors.phone)} ${createErrors.phone ? 'mb-1' : 'mb-3'}`}
      />
      {createErrors.phone && <p className="field-error mb-3">{createErrors.phone}</p>}

      <label className="label">Relationship *</label>
      <RelationshipTypeSelect
        value={createForm.relationship_type}
        onChange={(v) => {
          setCreateForm({ ...createForm, relationship_type: v });
          if (createErrors.relationship_type) {
            setCreateErrors({ ...createErrors, relationship_type: undefined });
          }
        }}
        hasError={!!createErrors.relationship_type}
        className={createErrors.relationship_type ? 'mb-1' : 'mb-4'}
      />
      {createErrors.relationship_type && (
        <p className="field-error mb-4">{createErrors.relationship_type}</p>
      )}

      <p className="label">Invitation Method *</p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => setCreateForm({ ...createForm, invitation_method: 'email' })}
          className={`${METHOD_CARD} ${
            createForm.invitation_method === 'email' ? METHOD_ON : METHOD_OFF
          }`}
        >
          <Mail className={`mt-0.5 h-4 w-4 flex-shrink-0 ${createForm.invitation_method === 'email' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Email Invitation</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Send setup link to caregiver&rsquo;s email
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setCreateForm({ ...createForm, invitation_method: 'in_clinic' })}
          className={`${METHOD_CARD} ${
            createForm.invitation_method === 'in_clinic' ? METHOD_ON : METHOD_OFF
          }`}
        >
          <UserPlus className={`mt-0.5 h-4 w-4 flex-shrink-0 ${createForm.invitation_method === 'in_clinic' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">In-Clinic Setup</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Set a temporary PIN for immediate setup
            </p>
          </div>
        </button>
      </div>

      {createForm.invitation_method === 'in_clinic' && (
        <div className="mb-3">
          <label className="label">Temporary PIN</label>
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              A secure temporary PIN will be generated and shown on the next screen, along with a setup QR code. Write it down or share it with the caregiver then. The system will not email it, and once the dialog closes you cannot view it again.
            </span>
          </div>
          <p className="hint">
            The caregiver will be prompted to change this PIN on first login.
          </p>
        </div>
      )}

      <label
        className={`${OPTION_BOX} mb-1 ${
          createErrors.informed_consent_verified
            ? 'border-danger-500 dark:border-danger-400'
            : 'border-gray-200 dark:border-gray-700'
        }`}
      >
        <input
          type="checkbox"
          checked={createForm.informed_consent_verified}
          onChange={(e) => {
            setCreateForm({ ...createForm, informed_consent_verified: e.target.checked });
            if (createErrors.informed_consent_verified) {
              setCreateErrors({ ...createErrors, informed_consent_verified: undefined });
            }
          }}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded"
        />
        <span className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-gray-100">Informed Consent Verified *</strong>
          <br />
          I confirm that informed consent has been properly recorded on paper for this caregiver to participate.
        </span>
      </label>
      {createErrors.informed_consent_verified ? (
        <p className="field-error mb-5">{createErrors.informed_consent_verified}</p>
      ) : (
        <div className="mb-5" />
      )}

      <div className="flex justify-between gap-2">
        <button
          type="button"
          onClick={() => setStep(STEP_SEARCH)}
          className="btn btn-secondary"
        >
          Back to Search
        </button>
        <button
          type="button"
          onClick={submitCreate}
          disabled={submitting || !createForm.informed_consent_verified}
          className="btn btn-primary"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : createForm.invitation_method === 'email' ? (
            'Create & Send Invite'
          ) : (
            'Create & Set PIN'
          )}
        </button>
      </div>
    </>
  );

  const renderSuccess = () => {
    const cg = successPayload?.caregiver;
    const cgName =
      cg?.full_name || `${cg?.first_name || ''} ${cg?.last_name || ''}`.trim() || '—';
    const relationship =
      successPayload?.kind === 'linked'
        ? linkForm.relationship_type
        : successPayload?.relationship_type;
    // A freshly-linked existing caregiver already has their own account → Active.
    // A newly-created caregiver hasn't set their own PIN yet → Pending First Login.
    const statusLabel = successPayload?.kind === 'linked' ? 'Active' : 'Pending First Login';
    // In-clinic creates render the shared "Caregiver Setup Ready" modal instead
    // of this inline success (handled before the overlay below), so this view
    // only covers email invites and linked existing caregivers.
    return (
      <>
        <Header title="Add Caregiver" />
        <div className="text-center pt-2 pb-4">
          <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-primary-600 dark:text-primary-400" strokeWidth={1.75} />
          <p className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-50">
            {successPayload?.kind === 'linked'
              ? 'Caregiver Linked Successfully'
              : 'Caregiver Created Successfully'}
          </p>
          {successPayload?.kind === 'linked' ? (
            <>
              {participantLabel && cgName !== '—' && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  {cgName} has been linked to {participantLabel}.
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                They will receive a notification and can access assessments using their
                existing account.
              </p>
            </>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {successPayload?.invitation_method === 'email'
                ? 'An invite link has been sent to the caregiver’s email. They’ll set their PIN on first login.'
                : 'Share this setup information with the caregiver to complete setup.'}
            </p>
          )}
        </div>
        {cg && (
          <div className="mb-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <div>
              <p className={VALUE_LABEL}>Caregiver</p>
              <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{cgName}</p>
            </div>
            <div>
              <p className={VALUE_LABEL}>Caregiver ID</p>
              <p className="mt-0.5 font-mono text-sm text-primary-700 dark:text-primary-400">
                {cg.caregiver_id}
              </p>
            </div>
            <div>
              <p className={VALUE_LABEL}>Relationship</p>
              <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{relationship || '—'}</p>
            </div>
            <div>
              <p className={VALUE_LABEL}>Status</p>
              <div className="mt-0.5"><CaregiverStatusBadge status={statusLabel} /></div>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onCreated && onCreated()}
            className="btn btn-primary"
          >
            Done
          </button>
        </div>
      </>
    );
  };

  // Stop Enter inside any modal input from bubbling out to a parent <form>.
  // Without this, the AddParticipantToSite form would submit when the SC hits
  // Enter while typing in the modal's search box.
  const swallowEnter = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type !== 'submit') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // In-clinic create success reuses the shared "Caregiver Setup Ready" modal so
  // the standalone Add Caregiver flow matches Add Participant (FECOA-1583): green
  // header, centered QR, Caregiver ID + Temporary PIN, instructions, and Done.
  if (
    step === STEP_SUCCESS &&
    successPayload?.kind === 'created' &&
    successPayload?.invitation_method === 'in_clinic' &&
    successPayload?.temp_pin &&
    successPayload?.caregiver?.caregiver_id
  ) {
    const cg = successPayload.caregiver;
    const cgName =
      cg.full_name || `${cg.first_name || ''} ${cg.last_name || ''}`.trim() || null;
    return (
      <CaregiverSetupModal
        caregivers={[
          {
            caregiverId: cg.caregiver_id,
            tempPin: successPayload.temp_pin,
            name: cgName,
            relationship: successPayload.relationship_type,
          },
        ]}
        studyCode={studyCode}
        sponsorId={getActiveSponsorId()}
        onClose={() => onCreated && onCreated()}
      />
    );
  }

  return (
    // Overlay scrolls (not the card) so a tall form is always fully reachable:
    // the modal centers when it fits and flows into a scrollable page when the
    // viewport is short, instead of clipping the footer at max-h.
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-gray-950/40 dark:bg-black/70"
      onKeyDown={swallowEnter}
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="my-8 w-full max-w-lg animate-slide-up rounded-xl bg-white p-6 shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
          {step === STEP_SEARCH && renderSearch()}
          {step === STEP_LINK_FORM && renderLinkForm()}
          {step === STEP_CREATE_FORM && renderCreateForm()}
          {step === STEP_SUCCESS && renderSuccess()}
        </div>
      </div>
    </div>
  );
};

export default AddCaregiverModal;
